import { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../config";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { authFetch } from "../utils/auth";
import LoadingScreen from "../components/common/LoadingScreen";

import './../assets/css/flashcardgame.css';
import { fillPlaceholdersForCard, loadLanguagePlaceholderMap, resolveCardDisplayText, scanCardPlaceholders, shiftCards } from "../utils/vocabGameHelpers";
import { speakText, stopSpeech } from "../utils/audioHelpers";
import SpeakerButton from "../components/common/SpeakerButton";
import InGameSettingsModal, { SettingsIcon } from "../components/common/InGameSettingsModal";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import SessionCompleteCard from "../components/common/SessionCompleteCard";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
function getLangTag(langId) { return LANG_TAG[parseInt(langId, 10)] ?? "de-DE"; }

function FlashCardGame() {
    const urlParams = new URLSearchParams(window.location.search);
    const languageId = getEffectiveLangId(urlParams.get("lang_id"));
    const langTag = getLangTag(languageId);
    const prevCardIdRef = useRef(null);

    const MINIMUM_COMPLETION_SCORE = 0.8; // Meaning, 80% of the deck has to be completed as easy

    const DEFAULT_DIFFICULTY = 5;
    const DEFAULT_STABILITY = 6;
    const DEFAULT_TIME_INTERVAL = 20;
    const MIN_STABILITY = 4;

    // Factors from global_vars.py (approximate reasonable values)
    const VE_DIFFICULTY_SHIFT = -3.0;
    const GO_DIFFICULTY_SHIFT = -0.5;
    const HA_DIFFICULTY_SHIFT = 1.0;
    const IM_DIFFICULTY_SHIFT = 2.0;

    const VE_STDIFF_FACTOR = 1.0;
    const GO_STDIFF_FACTOR = 0.5;
    const HA_STDIFF_FACTOR = -0.5;
    const IM_STDIFF_FACTOR = -0.8;

    const TIME_DIFFICULTY_FACTOR = 0.5;

    const EASY_THRESHOLD = 3;

    const [cards, setCards] = useState([]);
    const [current, setCurrent] = useState(0);
    const [showTranslation, setShowTranslation] = useState(false);
    const [error, setError] = useState(false);
    const [sessionDone, setSessionDone] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [flipRandomly, setFlipRandomly] = useState(false);
    const [autoPlayAudio, setAutoPlayAudio] = useState(false);
    const [flipped, setFlipped] = useState(false); // true = show translation first

    const [placeholderMap, setPlaceholderMap] = useState({});
    const numPlaceholders = 5; // Number of placeholders to use

    // Load vocab and initialize cards
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const lessonId = urlParams.get("lesson_id");
        const languageId = urlParams.get("lang_id");
        if (lessonId) {
            fetch(`${API_BASE_URL}/api/vocab?tile_id=${lessonId}`)
                .then(res => res.json())
                .then(data => {
                    // Add difficulty, stability, last_shown to each card
                    const initialCards = data.vocab.map(card => ({
                        ...card,
                        difficulty: DEFAULT_DIFFICULTY,
                        stability: DEFAULT_STABILITY,
                        last_shown: 0,
                        ever_practiced: false,
                        placeholders: [] // Contains all the placeholders that the card contains as list of {p: "\0", w: "Heinrich"}
                    }));

                    const placeholders = scanCardPlaceholders(initialCards);

                    const placeholderPromise = loadLanguagePlaceholderMap(languageId, placeholders, numPlaceholders)
                        .then(randomCodes => {
                            setPlaceholderMap(randomCodes);
                            return randomCodes;
                        })
                        .catch(err => {
                            console.error("Failed to load placeholders for language:", languageId, err);
                            return {};
                        });

                    const famPromise = authFetch(`${API_BASE_URL}/api/prompt-familiarity?tile_id=${lessonId}`)
                        .then(r => r.json())
                        .catch(() => ({ familiarity: [] }));

                    Promise.all([placeholderPromise, famPromise]).then(([randomCodes, famData]) => {
                        const practicedIds = new Set(
                            (famData.familiarity || [])
                                .filter(f => f.last_practiced != null)
                                .map(f => f.prompt_id)
                        );
                        const mergedCards = initialCards.map(c =>
                            practicedIds.has(c.id) ? { ...c, ever_practiced: true } : c
                        );
                        if (mergedCards.length > 0) {
                            fillPlaceholdersForCard(mergedCards[0], randomCodes);
                        }
                        setCards(mergedCards);
                    });
                })
                .catch((e) => {
                    setError(true)
                    console.log(e);
                });
        } else {
            setError(true);
        }
        return () => stopSpeech();
    }, []);

    // When a new card is shown, decide if it should be flipped
    useEffect(() => {
        let newFlipped = flipped;
        if (cards.length > 0 && !showTranslation) {
            if (flipRandomly) {
                newFlipped = Math.random() < 0.5;
                setFlipped(newFlipped);
            } else {
                newFlipped = false;
                setFlipped(false);
            }
        }

        const card = cards[0];
        const cardId = card?.id ?? null;
        const cardChanged = cardId !== null && cardId !== prevCardIdRef.current;
        if (cardId !== null) prevCardIdRef.current = cardId;

        let timeoutId;
        if (autoPlayAudio && cardChanged && card && !newFlipped) {
            const { prompt } = resolveCardDisplayText(card);
            timeoutId = setTimeout(() => speakText(prompt, langTag), 300);
        }

        return () => { if (timeoutId) clearTimeout(timeoutId); };
        // eslint-disable-next-line
    }, [current, cards.length, flipRandomly, showTranslation, autoPlayAudio]);

    // Helper: check if all cards are "easy"
    function allEasy(cardsArr) {
        return cardsArr.every(card => card.difficulty <= EASY_THRESHOLD);
    }

    // Save familiarity score for a given card list and completion status
    function saveFamiliarity(cardsArr) {
        const urlParams = new URLSearchParams(window.location.search);
        const tileId = urlParams.get("lesson_id");
        if (!tileId) return;

        // Average familiarity:, easy = 1, not easy = 0
        const studied = cardsArr;
        if (studied.length === 0) return;
        const avgScore = studied.reduce((sum, c) => sum + (c.difficulty <= EASY_THRESHOLD ? 1 : 0), 0) / studied.length;
        const score = Math.max(0, Math.min(1, avgScore));

        authFetch(`${API_BASE_URL}/api/upsert-familiarity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                tile_id: parseInt(tileId),
                familiarity_score: parseFloat(score.toFixed(3)),
                completed: score >= MINIMUM_COMPLETION_SCORE
            })
        }).catch(e => console.warn("Failed to save familiarity:", e));
    }

    // Handle rating
    function handleRating(rating) {
        let updatedCards = [...cards];
        let idx = 0; // Always rate the first card in the list

        let card = updatedCards[idx];

        let time_passed = card.last_shown > 0 ? (current - card.last_shown) : DEFAULT_TIME_INTERVAL;
        card.last_shown = current + 1;

        if (rating === 1) {
            card.stability *= 1 + VE_STDIFF_FACTOR * (1.1 - card.difficulty / 10);
            card.difficulty = Math.max(card.difficulty + VE_DIFFICULTY_SHIFT * TIME_DIFFICULTY_FACTOR * Math.log10(time_passed + 1), 0);
        } else if (rating === 2) {
            card.stability *= 1 + GO_STDIFF_FACTOR * (1.1 - card.difficulty / 10);
            card.difficulty = Math.max(card.difficulty + GO_DIFFICULTY_SHIFT * TIME_DIFFICULTY_FACTOR * Math.log10(time_passed + 1), 0);
        } else if (rating === 3) {
            card.stability *= 1 + HA_STDIFF_FACTOR * (1.1 - card.difficulty / 10);
            card.difficulty = Math.min(card.difficulty + HA_DIFFICULTY_SHIFT * TIME_DIFFICULTY_FACTOR / Math.log10(time_passed + 1), 10);
        } else if (rating === 4) {
            card.stability *= 1 + IM_STDIFF_FACTOR * (0.1 + card.difficulty / 10);
            card.difficulty = Math.min(card.difficulty + IM_DIFFICULTY_SHIFT * TIME_DIFFICULTY_FACTOR / Math.log10(time_passed + 1), 10);
        }

        updatedCards[idx] = card;

        // Fire-and-forget prompt familiarity upsert
        if (card.id != null) {
            authFetch(`${API_BASE_URL}/api/upsert-prompt-familiarity`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    prompt_id: card.id,
                    familiarity_score: 1 - (card.difficulty / 10)
                })
            }).catch(e => console.warn("Failed to save prompt familiarity:", e));
        }

        // Move card to new position based on stability (rounded)
        const newPos = Math.max(MIN_STABILITY, Math.round(card.stability));
        updatedCards = shiftCards(updatedCards, idx, newPos);

        // If all cards are easy, end session
        if (allEasy(updatedCards)) {
            setCards(updatedCards);
            saveFamiliarity(updatedCards);
            setSessionDone(true);
            return;
        }

        // Check if placeholders array isn't empty:
        fillPlaceholdersForCard(updatedCards[idx], placeholderMap);

        // Go to next card (wrap if needed)
        setCards(updatedCards);
        setCurrent(current + 1);
        setShowTranslation(false);

        //console.log(`Updated cards: `, updatedCards);
    }

    function handleShowTranslation() {
        setShowTranslation(true);
    }

    function handleExit() {
        saveFamiliarity(cards);
        setSessionDone(true);
    }

    function goToDashboard() {
        navigateToDashboard("vocabulary");
    }

    if (error) {
        return <div><p className="error">Error loading vocabulary data.</p></div>;
    }

    // Settings menu modal
    const settingsMenu = (
        <InGameSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            title="Settings"
        >
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={flipRandomly}
                        onChange={e => setFlipRandomly(e.target.checked)}
                        className="in-game-settings-checkbox"
                    />
                    Flip cards randomly
                </label>
                <div className="in-game-settings-desc">
                    Sometimes show translation first instead of prompt
                </div>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={autoPlayAudio}
                        onChange={e => setAutoPlayAudio(e.target.checked)}
                        className="in-game-settings-checkbox"
                    />
                    Auto-play pronunciation
                </label>
                <div className="in-game-settings-desc">
                    Automatically speak each card when it appears
                </div>
            </div>
        </InGameSettingsModal>
    );

    if (cards.length === 0) {
        return (
            <LoadingScreen
                title="Loading vocabulary..."
                onBack={goToDashboard}
                backLabel="Dashboard"
                onSettingsClick={() => setShowSettings(true)}
                settingsMenu={settingsMenu}
            />
        );
    }

    if (sessionDone) {
        const easyCount = cards.filter(c => c.difficulty <= EASY_THRESHOLD).length;
        const totalCount = cards.filter(c => c.last_shown !== 0).length;
        const easyPercent = totalCount > 0 ? Math.round((easyCount / totalCount) * 100) : 0;
        return (
            <SessionCompleteCard
                title="Study Session Complete"
                stats={[
                    { label: "Cards studied", value: totalCount },
                    { label: "Marked easy", value: `${easyCount} (${easyPercent}%)` }
                ]}
                onBack={goToDashboard}
            />
        );
    }

    const card = cards[0];

    // Calculate easy progress
    const easyCount = cards.filter(c => c.difficulty <= EASY_THRESHOLD).length;
    const totalCount = cards.length;
    const easyPercent = totalCount > 0 ? Math.round((easyCount / totalCount) * 100) : 0;

    const { prompt: promptToDisplay, translation: translationToDisplay } = resolveCardDisplayText(card);
    const frontText = flipped ? translationToDisplay : promptToDisplay;
    const frontLang = flipped ? "en-US" : langTag;
    const backText = flipped ? promptToDisplay : translationToDisplay;
    const backLang = flipped ? langTag : "en-US";

    return (
        <div className="flashcard-game" style={{ position: 'relative' }}>
            <BackToDashboardButton floating onClick={goToDashboard} />
            <button
                type="button"
                aria-label="Settings"
                className="in-game-settings-btn"
                onClick={() => setShowSettings(true)}
            >
                <SettingsIcon size={28} />
            </button>
            {settingsMenu}
            <h1>Vocabulary Flashcards</h1>
            <div className="flashcard-subtitle">
                {flipped
                    ? <>Translate and <strong>pronounce the prompt out loud</strong>. Click "Show Prompt" to check your answer, then <strong>rate the difficulty</strong> to continue. Exit anytime.</>
                    : <>Translate and <strong>pronounce the prompt out loud</strong>. Click "Show Translation" to check your answer, then <strong>rate the difficulty</strong> to continue. Exit anytime.</>
                }
            </div>
            <div className="easy-progress-bar-container">
                <div className="easy-progress-bar">
                    <div className="easy-progress-bar-fill" style={{ width: `${easyPercent}%` }}></div>
                    <span className="easy-progress-bar-text">{easyCount} / {totalCount} ({easyPercent}%)</span>
                </div>
            </div>
            <div className="flashcard-box">
                <div className="flashcard-prompt">
                    <strong>{frontText}</strong>
                    {card.last_shown === 0 && !card.ever_practiced && (
                        <span className="new-icon" title="First time this card is shown">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
                                <circle cx="10" cy="10" r="9" fill="#38f9d7" stroke="#155724" strokeWidth="2" />
                                <path d="M10 5.5V11M10 14.2V14.3" stroke="#155724" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            New!
                        </span>
                    )}
                    <SpeakerButton text={frontText} lang={frontLang} label="Hear pronunciation" className="speaker-btn--card-front" />
                </div>
                {showTranslation ? (
                    <div className="flashcard-translation">
                        <strong>{flipped ? "Prompt" : "Translation"}:</strong> {backText}
                        <SpeakerButton text={backText} lang={backLang} label="Hear pronunciation" className="speaker-btn--card-back" />
                    </div>
                ) : (
                    <button onClick={handleShowTranslation} className="show-translation-btn">{flipped ? 'Show Prompt' : 'Show Translation'}</button>
                )}
            </div>
            {showTranslation && (
                <div className="rating-section">
                    <p>How difficult was this card?</p>
                    <div className="rating-buttons">
                        <button className="btn-easy" onClick={() => handleRating(1)}>Very Easy</button>
                        <button className="btn-good" onClick={() => handleRating(2)}>Good</button>
                        <button className="btn-hard" onClick={() => handleRating(3)}>Hard</button>
                        <button className="btn-noclue" onClick={() => handleRating(4)}>No Clue</button>
                    </div>
                </div>
            )}
            <button onClick={handleExit} className="exit-btn" style={{ marginTop: '20px' }}>Exit</button>
        </div>
    );
}

export default FlashCardGame;
